import { CommonModule } from '@angular/common';
import { Component, effect, inject, input, OnInit, signal } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { NgxMaskPipe } from 'ngx-mask';
import { CarouselComponent } from 'src/app/layouts/carousel/carousel.component';
import { GetResumeCandidatePersonalInfo, Resume, ResumeCertification, ResumeEducation, ResumeEmployment, ResumeLanguage, ResumeReference, ResumeSkill } from 'src/app/models/resume/resume';
import { ResumeService } from 'src/app/services/resume.service';
import { environment } from 'src/environments/environment';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-cv-preview-candidates',
  templateUrl: './cv-preview-candidates.component.html',
  styles: [],
  standalone: true,
  imports: [CarouselComponent, CommonModule, NgxMaskPipe]
})
export class CvPreviewCandidatesComponent {
  public resumeId = input<number | null>(null);
  private readonly resumeService = inject(ResumeService);
  private readonly router = inject(Router);

  candidatePersonalInfo = signal<GetResumeCandidatePersonalInfo>({
    personId: 0,
    userId: 0,
    nationalityId: null,
    nationality: '',
    professionalProfile: '',
    cedulaFileId: null,
    passportFileId: null,
    wantToPublishOffers: false,
    wantToSearchJobs: false,
    isIndependentProfessional: false,
    photoFileId: null,
    sexId: null,
    sex: '',
    birthDate: null,
    primaryPhone: '',
    cellPhone: '',
    cellPhoneHasWhatsapp: false,
    userName: '',
    firstName: '',
    lastName: '',
    email: '',
    typeIdentificationId: null,
    typeIdentification: '',
    identification: '',
    addressId: null,
    countryId: null,
    countryName: '',
    stateId: null,
    stateName: '',
    cityId: null,
    city: '',
    address: '',
    latitude: null,
    longitude: null,
    hasVehicle: false,
    hasDriverLicense: false
  });
  personAge = signal<number | null>(null);
  public resume = signal<Resume>({
    id: 0,
    name: '',
    description: '',
    isEnabled: false,
    isPublic: false,
    isDefault: false,
    templateId: null,
    typeId: 0,
    travelAvailability: 0,
    salaryExpectationId: null,
    workPermit: false,
    publishResumeAsFreelancer: false,
    declareInformationIsTrue: false,
    consentForSelectionProcess: false,
    views: null,
    offersApplied: null,
    createDate: '',
    modifyDate: null,
    templatePreviewId: null,
    chargeTypeId: null,
    amountToChargePerType: null
  });

  educations = signal<ResumeEducation[]>([]);
  skills = signal<ResumeSkill[]>([]);
  languages = signal<ResumeLanguage[]>([]);
  employments = signal<ResumeEmployment[]>([]);
  certifications = signal<ResumeCertification[]>([]);
  references = signal<ResumeReference[]>([]);

  API_URL = environment.api.url;

  isLoading = signal<boolean>(false);
  constructor() {
    effect(() => {
      if (this.resumeId()) {
        this.getResume();
      }
    })
  }

  getResumeCandidatePersonalInfo() {
    this.resumeService.getResumeCandidatePersonalInfo().subscribe({
      next: res => {
        if (res) {
          this.candidatePersonalInfo.set(res);
          const birthDate = this.candidatePersonalInfo().birthDate;
          if (birthDate) {
            const bdate = new Date(birthDate);
            const diff = Date.now() - bdate.getTime();
            const age = new Date(diff).getUTCFullYear() - 1970;
            this.personAge.set(age);
          };
        }
      }
    });
  }

  getResume() {
    const resumeId = this.resumeId();
    if (!resumeId) {
      return;
    }
    this.resumeService.getResumeById(resumeId).subscribe({
      next: res => {
        this.resume.set(res);
        this.getResumeCandidatePersonalInfo();
        this.getResumeEducations();
        this.getResumeSkills();
        this.getResumeLanguages();
        this.getResumeEmployments();
        this.getResumeCertifications();
        this.getResumeReferences();
      }, error: (error: string[]) => {
        this.router.navigate(['candidates/my-cvs']);
        Swal.fire({
          title: 'Error',
          text: error[0],
          icon: 'error',
          confirmButtonColor: '#d33'
        });
      }
    });
  }

  getResumeEducations() {
    const resumeId = this.resumeId();
    if (!resumeId) {
      return;
    }
    this.resumeService.getResumeEducations(resumeId).subscribe({
      next: res => {
        this.educations.set(res);
      }
    });
  }

  getResumeSkills() {
    const resumeId = this.resumeId();
    if (!resumeId) {
      return;
    }
    this.resumeService.getResumeSkills(resumeId).subscribe({
      next: res => {
        this.skills.set(res);
      }
    });
  }

  getResumeLanguages() {
    const resumeId = this.resumeId();
    if (!resumeId) {
      return;
    }
    this.resumeService.getResumeLanguages(resumeId).subscribe({
      next: res => {
        this.languages.set(res);
      }
    });
  }


  getResumeEmployments() {
    const resumeId = this.resumeId();
    if (!resumeId) {
      return;
    }
    this.resumeService.getResumeEmployments(resumeId).subscribe({
      next: res => {
        this.employments.set(res);
      }
    });
  }

  getResumeCertifications() {
    const resumeId = this.resumeId();
    if (!resumeId) {
      return;
    }
    this.resumeService.getResumeCertifications(resumeId).subscribe({
      next: res => {
        this.certifications.set(res);
      }
    });
  }

  getResumeReferences() {
    const resumeId = this.resumeId();
    if (!resumeId) {
      return;
    }
    this.resumeService.getResumeReferences(resumeId).subscribe({
      next: res => {
        this.references.set(res);
      }
    });
  }

  goBack(){
    this.router.navigate(['candidates/my-cvs']);
  }

  printResume() {
    const resumeId = this.resumeId();
    if (!resumeId) {
      return;
    }
    this.isLoading.set(true);
    this.resumeService.generateResumePdf(resumeId).subscribe((response) => {
      this.isLoading.set(false);
      const file = new Blob([response], { type: 'application/pdf' }); // Crear el blob del PDF.
      const fileURL = URL.createObjectURL(file); // Crear la URL del blob.

      const iframe = document.getElementById('docPrintDocumento') as HTMLIFrameElement;
      if (iframe) {
        iframe.setAttribute('src', fileURL);
      }

      let modalRiesgo: any = new (window as any).bootstrap.Modal('#modalDocumento', {});
      modalRiesgo.show();
    }, () => {
      this.isLoading.set(false);
    });
  }

}